"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, Phone, Clock, Building2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface CountyArea {
  id: string;
  name: string;
  summary: string;
  leadTime: string;
  x: number;
  y: number;
}
interface CountyTown {
  countyId: string;
  name: string;
  to: string;
}
const countyAreas: CountyArea[] = [{
  id: "berkshire",
  name: "Berkshire",
  summary: "Primary operational hub covering major transport corridors and town centres.",
  leadTime: "Prompt site survey arranged",
  x: 44,
  y: 48
}, {
  id: "surrey",
  name: "Surrey",
  summary: "Dedicated crews for high-spec residential, commercial, and roofing projects.",
  leadTime: "Prompt site survey arranged",
  x: 58,
  y: 62
}, {
  id: "hampshire",
  name: "Hampshire",
  summary: "Rapid response across North and East Hampshire for contractors and developers.",
  leadTime: "Prompt site survey arranged",
  x: 34,
  y: 68
}, {
  id: "buckinghamshire",
  name: "Buckinghamshire",
  summary: "Commercial framework and residential access throughout South Bucks.",
  leadTime: "Prompt site survey arranged",
  x: 48,
  y: 28
}, {
  id: "london",
  name: "Greater London & South East",
  summary: "Pavement licenses, planned night work, and complex urban access schemes.",
  leadTime: "Prompt site survey arranged",
  x: 72,
  y: 45
}];
const countyTowns: CountyTown[] = [
// Berkshire
{
  countyId: "berkshire",
  name: "Reading",
  to: "/ScaffoldingInReadingPage"
}, {
  countyId: "berkshire",
  name: "Bracknell",
  to: "/ScaffoldingInBracknellPage"
}, {
  countyId: "berkshire",
  name: "Ascot",
  to: "/ScaffoldingInAscotPage"
}, {
  countyId: "berkshire",
  name: "Wokingham",
  to: ""
}, {
  countyId: "berkshire",
  name: "Maidenhead",
  to: ""
}, {
  countyId: "berkshire",
  name: "Windsor",
  to: ""
}, {
  countyId: "berkshire",
  name: "Newbury",
  to: ""
}, {
  countyId: "berkshire",
  name: "Slough",
  to: ""
},
// Surrey
{
  countyId: "surrey",
  name: "Guildford",
  to: "/ScaffoldingInGuildfordPage"
}, {
  countyId: "surrey",
  name: "Woking",
  to: "/ScaffoldingInWokingPage"
}, {
  countyId: "surrey",
  name: "Camberley",
  to: "/ScaffoldingInCamberleyPage"
}, {
  countyId: "surrey",
  name: "Farnham",
  to: ""
}, {
  countyId: "surrey",
  name: "Epsom",
  to: ""
}, {
  countyId: "surrey",
  name: "Redhill",
  to: ""
}, {
  countyId: "surrey",
  name: "Weybridge",
  to: ""
},
// Hampshire
{
  countyId: "hampshire",
  name: "Basingstoke",
  to: "/ScaffoldingInBasingstokePage"
}, {
  countyId: "hampshire",
  name: "Farnborough",
  to: "/ScaffoldingInFarnboroughPage"
}, {
  countyId: "hampshire",
  name: "Fleet",
  to: "/ScaffoldingInFleetPage"
}, {
  countyId: "hampshire",
  name: "Aldershot",
  to: ""
}, {
  countyId: "hampshire",
  name: "Andover",
  to: ""
}, {
  countyId: "hampshire",
  name: "Winchester",
  to: ""
},
// Buckinghamshire
{
  countyId: "buckinghamshire",
  name: "High Wycombe",
  to: "/ScaffoldingInHighWycombePage"
}, {
  countyId: "buckinghamshire",
  name: "Marlow",
  to: "/ScaffoldingInMarlowPage"
}, {
  countyId: "buckinghamshire",
  name: "Beaconsfield",
  to: "/ScaffoldingInBeaconsfieldPage"
}, {
  countyId: "buckinghamshire",
  name: "Amersham",
  to: ""
}, {
  countyId: "buckinghamshire",
  name: "Gerrards Cross",
  to: ""
}, {
  countyId: "buckinghamshire",
  name: "Aylesbury",
  to: ""
},
// Greater London
{
  countyId: "london",
  name: "Richmond",
  to: "/ScaffoldingInRichmondPage"
}, {
  countyId: "london",
  name: "Clapham",
  to: "/ScaffoldingInClaphamPage"
}, {
  countyId: "london",
  name: "Wandsworth",
  to: "/ScaffoldingInWandsworthPage"
}, {
  countyId: "london",
  name: "West London",
  to: ""
}, {
  countyId: "london",
  name: "South London",
  to: ""
}, {
  countyId: "london",
  name: "Heathrow Corridor",
  to: ""
}, {
  countyId: "london",
  name: "Kingston",
  to: ""
}, {
  countyId: "london",
  name: "M25 Orbital",
  to: ""
}];
const serviceHighlights = [{
  id: "nasc",
  title: "NASC & CHAS accredited",
  desc: "Fully accredited safety compliance and verified site safety practices."
}, {
  id: "timing",
  title: "Planned and Rapid Response",
  desc: "Scheduled long-term programmes with emergency cover available."
}, {
  id: "scope",
  title: "Commercial & Residential",
  desc: "From single domestic access towers to multi-lift commercial developments."
}];
export default function Coverage() {
  const [activeCountyId, setActiveCountyId] = useState<string>("berkshire");
  const selectedCounty = countyAreas.find(c => c.id === activeCountyId) || countyAreas[0];
  const selectedTowns = countyTowns.filter(t => t.countyId === selectedCounty.id);
  return <section id="coverage-map" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <p className="text-xs md:text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Operational coverage
            </p>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Scaffolding coverage across the South East
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            We provide commercial, residential, roofing, and solar scaffolding
            across Berkshire, Surrey, Hampshire, Buckinghamshire, and Greater London.
            Select a region below to review key towns and mobilization standards.
          </p>
        </div>

        {/* Interactive Layout Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left Column: Static Regional Map & Area Selector */}
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7 flex flex-col gap-4">
            {/* Map Frame Card */}
            <div className="relative bg-card text-card-foreground rounded-lg p-5 sm:p-6 border border-border shadow-sm overflow-hidden flex flex-col gap-4">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <h2 className="text-base sm:text-lg font-bold text-foreground">
                  BMF Scaffolding Coverage Area
                </h2>
              </div>

              {/* Static Coverage Map Image */}
              <div className="relative w-full aspect-[4/3] sm:aspect-[16/11] rounded-md overflow-hidden bg-muted/30 flex items-center justify-center">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/ChatGPT-Image-Sep-22-2026-04_32_18-PM.png" alt="BMF Scaffolding coverage map showing operating areas across Southern and South East England." loading="lazy" className="w-full h-full object-contain" />
              </div>
            </div>

            {/* Supporting Copy */}
            <p className="text-xs md:text-sm text-muted-foreground font-medium pt-1">
              Select an area below to view local coverage details.
            </p>

            {/* Quick County Selector Pills */}
            <div className="flex flex-wrap gap-2 pt-1">
              {countyAreas.map((county, i) => {
              const isSelected = activeCountyId === county.id;
              return <Button key={county.id} data-index={i} type="button" variant="ghost" onClick={() => setActiveCountyId(county.id)} className={`h-auto cursor-pointer text-xs md:text-sm font-medium px-3.5 py-1.5 rounded-md border transition-colors duration-150 ${isSelected ? "bg-primary text-primary-foreground border-primary shadow-xs hover:bg-primary hover:text-primary-foreground" : "bg-card text-card-foreground border-border hover:bg-muted"}`}>
                    {county.name}
                  </Button>;
            })}
            </div>
          </motion.div>

          {/* Right Column: Selected Region Specification & Town Hubs */}
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Active Detail Card */}
            <Card className="border border-border shadow-sm">
              <CardContent className="p-6 sm:p-7 flex flex-col gap-5">
                <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-primary">
                      Operational Region
                    </span>
                    <h3 className="text-2xl font-bold text-foreground mt-0.5">
                      {selectedCounty.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1 rounded bg-muted text-muted-foreground text-xs font-semibold">
                    <Clock className="size-3.5 text-primary" />
                    <span>{selectedCounty.leadTime}</span>
                  </div>
                </div>

                <p className="text-sm text-foreground/80 leading-relaxed">
                  {selectedCounty.summary}
                </p>

                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2.5">
                    Key Towns & Areas Served
                  </h4>
                  <div className="p-3.5 rounded-md bg-muted/60 border border-border">
                    <p className="text-sm font-medium text-foreground leading-relaxed">
                      {selectedTowns.map((town, idx) => <React.Fragment key={town.name}>
                          {town.to ? <Link to={town.to} className="text-primary hover:underline font-semibold">
                              {town.name}
                            </Link> : <span>{town.name}</span>}
                          {idx < selectedTowns.length - 1 && ", "}
                        </React.Fragment>)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2.5 pt-2 border-t border-border/60">
                      BMF provides scaffolding services across these areas from our regional operational bases rather than maintaining separate local branch offices.
                    </p>
                  </div>
                </div>

                <div className="space-y-2 pt-1 border-t border-border">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Domestic, commercial, roofing, and solar setups</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Full safety documentation, risk assessments, and handover certs</span>
                  </div>
                </div>

                {/* Conversion Actions */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                  <Button asChild className="w-full sm:w-auto">
                    <Link to="/ContactRequestQuotePage">
                      Request a quote
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full sm:w-auto">
                    <a href="tel:01344929900">
                      <Phone className="size-4 mr-1.5 text-primary" />
                      Speak to team
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Core Capability Callouts */}
            <div className="grid grid-cols-1 gap-3">
              {serviceHighlights.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-3 p-3.5 rounded-md bg-card border border-border">
                  <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">
                      {item.title}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Bottom Verification & Out-of-Area Notice */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs md:text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Building2 className="size-4 text-primary shrink-0" />
            <span>
              Operating across the South East with confirmed NASC and CHAS accreditations.
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span>Project located just outside these zones?</span>
            <Link to="/ContactRequestQuotePage" className="text-primary font-semibold hover:underline">
              Contact our contracts desk
            </Link>
          </div>
        </div>
      </div>
    </section>;
}